<div class="flex flex-col items-center justify-center min-h-[65vh] px-4 py-12">
  <div
    class="w-full max-w-lg surface-default border border-neutral-4 rounded-xl shadow-sm p-6 sm:p-8 text-left"
  >
    @switch (pageState()) {
      @case ('loading') {
        <div class="flex flex-col items-center justify-center py-10 gap-4 text-center">
          <mat-spinner diameter="44" class="inline-block"></mat-spinner>
          <div class="text-neutral-12 font-medium text-lg m-0">Chargement de votre invitation...</div>
          <p class="text-neutral-10 text-sm max-w-sm m-0">
            Veuillez patienter quelques instants pendant la vérification des informations du bien.
          </p>
        </div>
      }

      @case ('inspect') {
        @if (invitation(); as inv) {
          <div class="flex flex-col gap-6">
            <!-- En-tête de l'invitation (Sentence case obligatoire) -->
            <div class="flex flex-col gap-2">
              <div
                class="inline-flex items-center gap-1.5 self-start px-2.5 py-1 rounded-md bg-primary-1 dark:bg-primary-10/30 text-primary dark:text-primary-contrast text-xs font-semibold border border-primary-3 dark:border-primary-8"
              >
                <mat-icon class="text-sm !w-4 !h-4">diversity_3</mat-icon>
                <span>Invitation à la co-gestion</span>
              </div>

              <h1 class="text-2xl font-bold tracking-tight text-neutral-12 m-0">
                Vous avez reçu une invitation
              </h1>

              <p class="text-neutral-10 text-sm m-0">
                @if (inv.inviter?.firstName) {
                  <strong>{{ inv.inviter.firstName }} {{ inv.inviter.lastName }}</strong> vous invite à
                  co-gérer ce bien immobilier sur ETXE.
                } @else {
                  Vous êtes invité à participer à la gestion de ce bien immobilier sur ETXE.
                }
              </p>
            </div>

            <!-- Fiche récapitulative du bien -->
            <div
              class="p-4 rounded-lg bg-neutral-2 dark:bg-neutral-1 border border-neutral-4 flex flex-col sm:flex-row gap-4 items-start sm:items-center"
            >
              @if (inv.property?.coverPhotoUrl) {
                <img
                  [src]="inv.property.coverPhotoUrl"
                  [alt]="inv.property.title"
                  class="w-20 h-20 rounded-lg object-cover shrink-0 border border-neutral-4"
                />
              } @else {
                <div
                  class="w-16 h-16 sm:w-20 sm:h-20 rounded-lg bg-neutral-3 dark:bg-neutral-2 border border-neutral-4 flex items-center justify-center shrink-0 text-neutral-9"
                >
                  <mat-icon class="text-3xl !w-8 !h-8">home</mat-icon>
                </div>
              }

              <div class="flex flex-col gap-1 min-w-0 flex-1">
                <div class="flex items-center gap-2">
                  <span
                    class="inline-block px-2 py-0.5 rounded text-xs font-medium bg-neutral-3 text-neutral-11 dark:bg-neutral-2 dark:text-neutral-10"
                  >
                    {{ roleLabel() }}
                  </span>
                </div>
                <div class="font-semibold text-neutral-12 text-base truncate">
                  {{ inv.property.title }}
                </div>
                @if (inv.property.city) {
                  <div class="text-xs text-neutral-10 flex items-center gap-1">
                    <mat-icon class="text-xs !w-3.5 !h-3.5 text-neutral-9">place</mat-icon>
                    <span
                      >{{ inv.property.city }}
                      @if (inv.property.zipCode) {
                        ({{ inv.property.zipCode }})
                      }
                    </span>
                  </div>
                }
                <div class="text-xs text-neutral-9 mt-1">
                  Destinataire : <span class="font-medium text-neutral-11">{{ inv.email }}</span>
                </div>
              </div>
            </div>

            <!-- Workflow d'action selon l'état d'authentification -->
            @if (!isLoggedIn()) {
              <!-- Cas 1 : Visiteur non connecté mais possédant déjà un compte -->
              @if (inv.userExists) {
                <div
                  class="p-4 rounded-lg bg-blue-1/50 dark:bg-blue-11/10 border border-blue-4 flex flex-col gap-2"
                >
                  <div class="flex items-center gap-2 text-blue-11 dark:text-blue-9 font-medium text-sm">
                    <mat-icon class="text-base !w-4 !h-4">account_circle</mat-icon>
                    <span>Compte existant détecté</span>
                  </div>
                  <p class="text-neutral-11 text-xs m-0">
                    Un compte ETXE existe déjà pour l'adresse <strong>{{ inv.email }}</strong
                    >. Veuillez vous connecter pour répondre à cette invitation.
                  </p>
                </div>

                <div class="flex flex-col gap-2 pt-2">
                  <button
                    mat-flat-button
                    class="primary w-full !py-2.5 !rounded-lg text-sm font-medium"
                    (click)="onLogin()"
                  >
                    <mat-icon class="mr-1.5">login</mat-icon>
                    <span>Se connecter pour répondre</span>
                  </button>
                </div>
              } @else {
                <!-- Cas 2 : Visiteur non connecté et n'ayant pas encore de compte -->
                <div
                  class="p-4 rounded-lg bg-emerald-1/50 dark:bg-emerald-11/10 border border-emerald-4 flex flex-col gap-2"
                >
                  <div
                    class="flex items-center gap-2 text-emerald-11 dark:text-emerald-9 font-medium text-sm"
                  >
                    <mat-icon class="text-base !w-4 !h-4">how_to_reg</mat-icon>
                    <span>Création de compte nécessaire</span>
                  </div>
                  <p class="text-neutral-11 text-xs m-0">
                    Pour co-gérer ce bien immobilier, créez votre compte ETXE gratuitement avec l'adresse
                    <strong>{{ inv.email }}</strong
                    >.
                  </p>
                </div>

                <div class="flex flex-col gap-2 pt-2">
                  <button
                    mat-flat-button
                    class="primary w-full !py-2.5 !rounded-lg text-sm font-medium"
                    (click)="onSignUp()"
                  >
                    <mat-icon class="mr-1.5">person_add</mat-icon>
                    <span>Créer mon compte</span>
                  </button>
                  <p class="text-xs text-neutral-10 text-center m-0">
                    Vous avez déjà un compte ?
                    <button
                      type="button"
                      class="text-primary hover:underline font-medium bg-transparent border-0 p-0 cursor-pointer ml-1"
                      (click)="onLogin()"
                    >
                      Connectez-vous
                    </button>
                  </p>
                </div>
              }
            } @else {
              <!-- Cas 3 : Utilisateur connecté -->
              @if (isMatchingEmail()) {
                <div
                  class="p-4 rounded-lg bg-neutral-2 dark:bg-neutral-1 border border-neutral-4 text-xs text-neutral-10"
                >
                  En acceptant cette invitation, vous aurez accès aux informations complètes, aux documents et
                  au suivi des candidatures d'agents sur ce bien.
                </div>

                <div class="flex flex-col sm:flex-row gap-3 pt-2">
                  <button
                    mat-flat-button
                    class="primary flex-1 !py-2.5 !rounded-lg text-sm font-medium"
                    [disabled]="isSubmitting()"
                    (click)="onAccept()"
                  >
                    @if (isSubmitting()) {
                      <mat-spinner diameter="18" class="inline-block mr-2"></mat-spinner>
                    }
                    <mat-icon class="mr-1.5">check</mat-icon>
                    <span>Accepter la co-gestion</span>
                  </button>

                  <button
                    mat-stroked-button
                    class="flex-1 !py-2.5 !rounded-lg text-sm font-medium"
                    [disabled]="isSubmitting()"
                    (click)="onDecline()"
                  >
                    <mat-icon class="mr-1.5">close</mat-icon>
                    <span>Refuser l'invitation</span>
                  </button>
                </div>
              } @else {
                <!-- Cas 4 : Connecté avec une adresse différente de l'invitation -->
                <div
                  class="p-4 rounded-lg bg-amber-1/60 dark:bg-amber-11/15 border border-amber-4 flex flex-col gap-2 text-xs"
                >
                  <div class="flex items-center gap-2 text-amber-11 dark:text-amber-9 font-medium text-sm">
                    <mat-icon class="text-base !w-4 !h-4">warning</mat-icon>
                    <span>Compte connecté différent</span>
                  </div>
                  <p class="text-neutral-11 m-0">
                    Cette invitation est destinée à <strong>{{ inv.email }}</strong
                    >. Vous êtes actuellement connecté avec l'adresse
                    <strong>{{ currentUser()?.contactInfos?.email }}</strong
                    >.
                  </p>
                </div>

                <div class="flex flex-col gap-2 pt-2">
                  <button
                    mat-stroked-button
                    class="w-full !py-2.5 !rounded-lg text-sm font-medium"
                    (click)="onSwitchAccount()"
                  >
                    <mat-icon class="mr-1.5">swap_horiz</mat-icon>
                    <span>Changer de compte</span>
                  </button>
                </div>
              }
            }
          </div>
        }
      }

      @case ('accepted') {
        <div class="flex flex-col items-center gap-4 text-center py-4">
          <div
            class="w-14 h-14 rounded-xl bg-emerald-1 text-emerald-11 border border-emerald-4 flex items-center justify-center mb-1"
          >
            <mat-icon class="text-3xl !w-8 !h-8">check_circle</mat-icon>
          </div>

          <h1 class="text-2xl font-bold tracking-tight text-neutral-12 m-0">
            Invitation acceptée avec succès
          </h1>

          <p class="text-neutral-10 text-sm max-w-md m-0">
            Félicitations, vous participez désormais à la co-gestion de ce bien immobilier. Vous pouvez dès à
            présent consulter la fiche détaillée et suivre le projet de vente.
          </p>

          @if (invitation()?.property; as prop) {
            <div
              class="w-full my-3 p-4 rounded-lg bg-neutral-2 dark:bg-neutral-1 border border-neutral-4 text-left"
            >
              <div class="text-xs font-semibold uppercase tracking-wider text-neutral-9 mb-1">
                Bien immobilier
              </div>
              <div class="font-semibold text-neutral-12 text-base truncate">
                {{ prop.title }}
              </div>
              @if (prop.city) {
                <div class="text-xs text-neutral-10 mt-0.5 flex items-center gap-1">
                  <mat-icon class="text-xs !w-3.5 !h-3.5 text-neutral-9">place</mat-icon>
                  <span
                    >{{ prop.city }}
                    @if (prop.zipCode) {
                      ({{ prop.zipCode }})
                    }
                  </span>
                </div>
              }
            </div>
          }

          <div class="flex flex-col sm:flex-row gap-3 w-full mt-2">
            <button
              mat-flat-button
              class="primary w-full !py-2.5 !rounded-lg font-medium"
              (click)="goToProperty()"
            >
              <mat-icon class="mr-1.5">home</mat-icon>
              <span>Accéder au bien</span>
            </button>
          </div>
        </div>
      }

      @case ('declined') {
        <div class="flex flex-col items-center gap-4 text-center py-4">
          <div
            class="w-14 h-14 rounded-xl bg-neutral-2 dark:bg-neutral-2 text-neutral-11 border border-neutral-4 flex items-center justify-center mb-1"
          >
            <mat-icon class="text-3xl !w-8 !h-8">block</mat-icon>
          </div>

          <h1 class="text-2xl font-bold tracking-tight text-neutral-12 m-0">Invitation déclinée</h1>

          <p class="text-neutral-10 text-sm max-w-md m-0">
            Vous avez refusé de participer à la co-gestion de ce bien immobilier. L'invitation a été clôturée.
          </p>

          <div class="flex flex-col sm:flex-row gap-3 w-full mt-4">
            <button mat-stroked-button class="w-full !py-2.5 !rounded-lg font-medium" (click)="goToHome()">
              <span>Retour à l'accueil</span>
            </button>
          </div>
        </div>
      }

      @case ('error') {
        <div class="flex flex-col items-center gap-4 text-center py-4">
          <div
            class="w-14 h-14 rounded-xl bg-red-1 text-red-11 border border-red-4 flex items-center justify-center mb-1"
          >
            <mat-icon class="text-3xl !w-8 !h-8">error_outline</mat-icon>
          </div>

          <h1 class="text-2xl font-bold tracking-tight text-neutral-12 m-0">
            Impossible d'accéder à l'invitation
          </h1>

          <p class="text-neutral-10 text-sm max-w-md m-0">
            {{ errorMessage() }}
          </p>

          <div class="flex flex-col sm:flex-row gap-3 w-full mt-4">
            <button mat-stroked-button class="w-full !py-2.5 !rounded-lg font-medium" (click)="goToHome()">
              <span>Retour à l'accueil</span>
            </button>
          </div>
        </div>
      }
    }
  </div>
</div>
